import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, delay, map } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AsyncRequestService } from 'src/app/core/async-request/async-request.service';
import { WebSocketNotificationType } from 'src/app/core/websocket/model';
import { WebSocketService } from 'src/app/core/websocket/websocket.service';
import { AissitantConfigForTeam } from 'src/app/task-process/shared/models';
import { AIAssistantAnswerReliabilityEnum } from 'src/app/task-process/shared/models/ai-assistant-answer-reliability.enum';
import { AIAssistantAnswerStatusEnum } from 'src/app/task-process/shared/models/ai-assistant-answer-status.enum';
import { AIAssistantAnswer } from 'src/app/task-process/shared/models/ai-assistant-answer.model';
import { AIAssistantStatistic } from 'src/app/task-process/shared/models/ai-assistant-statistic.model';

import { API_URL_COMPANIES, API_URL_COMPANIES_V2, BasicService } from './basic.service';
import { ErrorHandlingService } from './error-handling.service';

@Injectable({ providedIn: 'root' })
export class SuggestAnswerService {
  AissitantConfigForTeamBS: Subject<AissitantConfigForTeam> = new Subject();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private asyncRequestService: AsyncRequestService,
    private httpClient: HttpClient,
    private errorHandlingService: ErrorHandlingService,
    private basicService: BasicService,
    private webSocketService: WebSocketService
  ) {}

  getSuggestAnswer(taskId: number) {
    this.basicService.answerSuggestionPending[taskId] = {
      resultBS: new BehaviorSubject(null),
      expiredSubscription: new Subscription(),
      received: false,
    };

    this.configureAndAttachAnExpirationToAnswer(taskId);

    this.asyncRequestService
      .post<AIAssistantAnswer>(
        `${API_URL_COMPANIES_V2}/${this.alcmeonContextService.companyId}/task/${taskId}/suggest-answer`,
        null
      )
      .pipe(catchError(response => of(response)))
      .subscribe(response => {
        if (response.status === 500) {
          this.webSocketService.message$.next({
            type: WebSocketNotificationType.AIAssistantSuggestion,
            data: { status: AIAssistantAnswerStatusEnum.FAILED },
            task_id: taskId,
          } as any);
        } else {
          this.webSocketService.message$.next({
            type: WebSocketNotificationType.AIAssistantSuggestion,
            data: { status: AIAssistantAnswerStatusEnum.STARTED },
            task_id: taskId,
          } as any);
        }
      });
  }

  private configureAndAttachAnExpirationToAnswer(taskId: number) {
    const jwtValidityInMs = 60000;
    this.basicService.answerSuggestionPending[taskId].expiredSubscription = of(1)
      .pipe(
        delay(jwtValidityInMs),
        map(() => {
          if (
            this.basicService.answerSuggestionPending[taskId] &&
            !this.basicService.answerSuggestionPending[taskId].received
          ) {
            this.webSocketService.message$.next({
              type: WebSocketNotificationType.AIAssistantSuggestion,
              data: { status: AIAssistantAnswerStatusEnum.EXPIRED },
              task_id: taskId,
            } as any);
          }
        })
      )
      .subscribe();
  }

  resetAIAssistantAnswerForTaskId(taskId: number) {
    this.basicService.resetAIAssistantAnswerForTaskId(taskId);
  }

  minimizeAIAssistantAnswer(taskId: number) {
    this.webSocketService.message$.next({
      type: WebSocketNotificationType.AIAssistantSuggestion,
      data: { status: AIAssistantAnswerStatusEnum.MINIMIZED },
      task_id: taskId,
    } as any);
  }

  restoreAIAssistantAnswer(taskId: number) {
    this.webSocketService.message$.next({
      type: WebSocketNotificationType.AIAssistantSuggestion,
      data: { status: AIAssistantAnswerStatusEnum.RESTORED },
      task_id: taskId,
    } as any);
  }

  useAIAssistantAnswerForTaskId(taskId: number, model: AIAssistantStatistic) {
    const { suggestion, suggestionId, answer, status: finalStatus } = model;
    let status: AIAssistantAnswerReliabilityEnum = finalStatus;
    if (!status) {
      status =
        suggestion === answer ? AIAssistantAnswerReliabilityEnum.ACCEPTED : AIAssistantAnswerReliabilityEnum.EDITED;
    }
    this.notifyAIAssistantAnswerForTaskId(taskId, {
      suggestion,
      suggestionId,
      status,
      answer,
    });
  }

  private notifyAIAssistantAnswerForTaskId(taskId: number, model: AIAssistantStatistic) {
    const { suggestion, suggestionId, status, answer } = model;
    this.httpClient
      .post(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/task/${taskId}/suggest-stats`, {
        suggestion,
        suggestion_id: suggestionId,
        status,
        answer,
      })
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError())
      // We do not care about the response
      .subscribe();
  }

  getAissitantConfigForTeam(routingTagId: number): Observable<AissitantConfigForTeam> {
    return this.httpClient
      .get<AissitantConfigForTeam>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/ai-settings/aissistant-config-by-tag-id/${routingTagId}`
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }
}
